import Breadcrumbs from 'components/Common/Breadcrumb';
import { convertEditorBlocksToHTML } from 'components/ContentCompiler';
import Loader from 'components/Loader';
import PageDetailCard from 'components/PageDetailsCard';
import SlugInput from 'components/SlugInput';
import ContentEditor from 'containers/blog/BlogList/editor';
import { useFormik } from 'formik';
import { useRouter } from 'hooks/useRouter';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import { useHistory } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { Button, Card, CardBody, Col, Container, Form, FormFeedback, FormGroup, Input, Label, Row } from 'reactstrap';
import { LANGUAGE_ARRAY_WITH_STRING, MODULE_TYPE, SCHOOL_FAQ_CATEGORIES } from 'utils/constants';
import { formatDate, titleCaseIfExists, urlSlugify } from 'utils/helper';
import * as Yup from 'yup';
import useRequest from '../../../hooks/useRequest';

const UpdateschoolFaq = () => {
  const router = useRouter();
  const history = useHistory();

  const faqID = router?.query?.id;
  const schoolId = router?.query?.schoolId;

  const editorInstance = useRef(null);

  const [faqDetails, setFaqDetails] = useState();

  const [getFaqRequest, { loading: getFaqLoading }] = useRequest({
    url: `/school/admin/faq/${faqID}`,
    method: 'GET',
    onSuccess: async (data) => {
      setFaqDetails(data);

      if (!editorInstance.current || !editorInstance.current.isReady) {
        return;
      }

      await editorInstance.current.isReady;
      if (!isEmpty(data?.contentJSON)) {
        await editorInstance.current.blocks.render({ blocks: data.contentJSON?.blocks });
      }
    },
    onError: (err) => {
      toast.error(err?.message?.message || err?.message);
    },
  });

  const [addFaqRequest, { loading: addFaqLoading }] = useRequest({
    url: '/school/admin/faq',
    method: 'POST',
    onSuccess: () => {
      history.replace(`/schools/edit/${schoolId}/faqs`);
      toast.success('Success! School FAQ has been added.');
    },
    onError: (err) => {
      toast.error(err?.message?.message || err?.message);
    },
  });
  const [updateFaqRequest, { loading: updateFaqLoading }] = useRequest({
    url: `/school/admin/faq/${faqID}`,
    method: 'PUT',
    onSuccess: () => {
      toast.success('Success! School FAQ has been updated.');
      window.location.reload();
    },
    onError: (err) => {
      toast.error(err?.message?.message || err?.message);
    },
  });

  const getFormikValues = (data) => {
    const parseDate = (str) => {
      if (!str) return null;
      const [day, month, year] = str.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return isNaN(date) ? null : date;
    };

    return {
      active: data?.active !== undefined ? data.active : true,
      deleted: data?.deleted !== undefined ? data.deleted : false,

      title: data?.title || '',
      slug: data?.slug || '',
      order: data?.order || '0',
      language: data?.language || LANGUAGE_ARRAY_WITH_STRING[0]?.value,
      content: data?.content || '',
      contentJSON: data?.contentJSON || {},

      category: !isEmpty(data?.category)
        ? {
            label: data?.category,
            value: data?.category,
          }
        : null,
      subCategory: !isEmpty(data?.subCategory)
        ? {
            label: data?.subCategory,
            value: data?.subCategory,
          }
        : null,
      metadata: !isEmpty(data?.metadata) ? JSON.stringify(data?.metadata, null, 2) : '{}',
      date: data?.metadata?.date ? parseDate(data?.metadata?.date) : null,
    };
  };

  const isJSON = (val) => {
    try {
      return typeof val === 'string' && JSON.parse(val);
    } catch (err) {
      toast.error(err);
      return false;
    }
  };

  const convertJSON = (val) => {
    try {
      const parseObj = JSON.parse(val);
      return parseObj;
    } catch (err) {
      toast.error(err);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: false,
    initialValues: !isEmpty(faqDetails) ? { ...getFormikValues(faqDetails) } : { ...getFormikValues() },
    onSubmit: async (values) => {
      if (!isJSON(values?.metadata)) {
        toast.error('Please enter a valid metadata JSON');
        return;
      }
      const updatedContentJSON = await editorInstance.current.save();

      let updatedMetadata = {
        ...(values?.metadata ? convertJSON(values?.metadata) : {}),
        ...(values?.date ? { date: formatDate(values?.date, 'DD-MM-YYYY') } : { date: undefined }),
      };

      const payload = {
        ...values,
        schoolId,

        category: values?.category?.label,
        subCategory: values?.subCategory?.value,

        content: convertEditorBlocksToHTML(updatedContentJSON),
        contentJSON: updatedContentJSON,
        metadata: updatedMetadata,

        date: undefined,
      };

      if (isEmpty(faqID)) {
        addFaqRequest({
          body: payload,
        });
      } else {
        updateFaqRequest({
          body: payload,
        });
      }
    },
    validationSchema: Yup.object().shape({
      title: Yup.string().required('Please provide title '),
      slug: Yup.string().required('Please provide slug'),
      category: Yup.object()
        .required('Please provide cateogry ')
        .nullable(true),
      subCategory: Yup.object()
        .required('Please provide Sub category ')
        .nullable(true),
    }),
  });

  useEffect(() => {
    if (!isEmpty(faqID)) {
      getFaqRequest();
    }
  }, [faqID]);

  return (
    <>
      <div className="page-content">
        <Loader isActive={getFaqLoading || addFaqLoading || updateFaqLoading} />
        <Container fluid>
          <Breadcrumbs title="School FAQ" breadcrumbItem={`${isEmpty(faqID) ? 'Add' : 'Edit'} School FAQ`} />

          <Form onSubmit={formik.handleSubmit}>
            <Row>
              <Col md={9}>
                <Card>
                  <CardBody>
                    <Row className="align-items-center">
                      <Col md={12}>
                        <FormGroup className="mb-4">
                          <Label for="title" className="form-label  ">
                            Question <span className="text-danger">*</span>
                          </Label>
                          <Input
                            id="title"
                            name="title"
                            type="text"
                            className="form-control"
                            placeholder="Enter question..."
                            invalid={!!(formik.touched.title && formik.errors.title)}
                            {...formik.getFieldProps('title')}
                          />
                          <FormFeedback>{formik.errors.title}</FormFeedback>
                        </FormGroup>
                      </Col>

                      <Col md={12}>
                        <FormGroup className="mb-4">
                          <SlugInput
                            required={true}
                            formik={formik}
                            ID={faqID}
                            nameValue={formik.values.title}
                            formGroupClassName={'mt-0'}
                          />
                        </FormGroup>
                      </Col>

                      <Col md={6}>
                        <FormGroup className="mb-4">
                          <Label for="date" className="form-label  ">
                            Pubished Date
                          </Label>
                          <DatePicker
                            maxDate={new Date()}
                            className="form-control"
                            selected={formik.values.date}
                            onChange={(date) => formik.setFieldValue('date', date)}
                            placeholderText="Select Date..."
                            dateFormat="dd-MM-yyyy"
                            isClearable
                          />
                          <FormFeedback>{formik.errors.date}</FormFeedback>
                        </FormGroup>
                      </Col>

                      <Col md={6}>
                        <FormGroup className="mb-4">
                          <Label for="order" className="form-label  ">
                            Order
                          </Label>
                          <Input
                            id="order"
                            name="order"
                            type="number"
                            className="form-control"
                            placeholder="Enter question..."
                            invalid={!!(formik.touched.order && formik.errors.order)}
                            {...formik.getFieldProps('order')}
                          />
                          <FormFeedback>{formik.errors.order}</FormFeedback>
                        </FormGroup>
                      </Col>

                      <Col md={12}>
                        <FormGroup className="mb-4">
                          <Label for="content" className="form-label  ">
                            Answer <span className="text-danger">*</span>
                          </Label>
                          <div className="form-control p-3 rounded-2">
                            <ContentEditor editorInstance={editorInstance} forType={MODULE_TYPE.METADATA} />
                          </div>
                          {formik.errors.content && formik.touched.content && (
                            <FormFeedback className="d-block">{formik.errors.content}</FormFeedback>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                <Card>
                  <CardBody>
                    <Col md={12}>
                      <FormGroup className="mb-4">
                        <Label for="metadata" className="form-label  ">
                          Meta Data
                        </Label>
                        <Input
                          rows={6}
                          id="metadata"
                          name="metadata"
                          type="textarea"
                          className="form-control"
                          placeholder="Meta Data..."
                          invalid={!!(formik.touched.metadata && formik.errors.metadata)}
                          {...formik.getFieldProps('metadata')}
                        />
                        <FormFeedback>{formik.errors.metadata}</FormFeedback>
                      </FormGroup>
                    </Col>
                  </CardBody>
                </Card>
              </Col>
              <Col md={3}>
                <Card>
                  <CardBody>
                    <div>
                      <Button type="submit" color="primary" className="mb-3 w-100">
                        {faqID ? 'Save School FAQ' : 'Add School FAQ'}
                      </Button>

                      <Button
                        className="w-100"
                        color="light"
                        onClick={() => {
                          history.replace(`/schools/edit/${schoolId}/faqs`);
                        }}>
                        Cancel
                      </Button>
                    </div>
                  </CardBody>
                </Card>

                <PageDetailCard data={faqDetails} />

                <Card>
                  <CardBody>
                    <Col md={12}>
                      <FormGroup className="mb-4">
                        <Label for="category" className="form-label  ">
                          Category <span className="text-danger">*</span>
                        </Label>
                        <Select
                          id="category"
                          options={SCHOOL_FAQ_CATEGORIES?.map((e) => ({
                            label: e?.category,
                            value: urlSlugify(e?.category),
                          }))}
                          value={formik.values.category}
                          onChange={(e) => formik.setFieldValue('category', e)}
                        />
                        {formik.errors.category && formik.touched.category && (
                          <FormFeedback className="d-block">{formik.errors.category}</FormFeedback>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md={12}>
                      <FormGroup className="mb-4">
                        <Label for="category" className="form-label  ">
                          Sub Category <span className="text-danger">*</span>
                        </Label>
                        <Select
                          id="category"
                          options={SCHOOL_FAQ_CATEGORIES?.find(
                            (e) => urlSlugify(e?.category) === formik.values.category?.value
                          )?.subcategories?.map((e) => ({
                            label: e,
                            value: urlSlugify(e),
                          }))}
                          value={formik.values.subCategory}
                          onChange={(e) => formik.setFieldValue('subCategory', e)}
                        />
                        {formik.errors.subCategory && formik.touched.subCategory && (
                          <FormFeedback className="d-block">{formik.errors.subCategory}</FormFeedback>
                        )}
                      </FormGroup>
                    </Col>
                  </CardBody>
                </Card>

                <Card>
                  <CardBody>
                    <Row>
                      {['active', 'deleted']?.map((elem, index) => (
                        <Col md={12}>
                          <FormGroup className={'mb-3'} key={index}>
                            <Label className="mb-3">{titleCaseIfExists(elem)} </Label>
                            <div className="d-flex align-items-center">
                              {[true, false].map((option, i) => (
                                <div key={`${elem}_${i}`} className="form-check d-inline-block me-4">
                                  <input
                                    type="radio"
                                    id={`${elem}_${option}`}
                                    name={elem}
                                    className="form-check-input"
                                    checked={formik.values[elem] === option}
                                    onChange={() => formik.setFieldValue(`${elem}`, option)}
                                  />
                                  <label className="form-check-label" htmlFor={`${elem}_${option}`}>
                                    {option ? 'Yes' : 'No'}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </FormGroup>
                        </Col>
                      ))}
                      <Col md={12}>
                        <Label className="mb-3">Language </Label>
                        <div>
                          {LANGUAGE_ARRAY_WITH_STRING?.map((option, i) => (
                            <div key={`language_${i}`} className="form-check mb-3 d-inline-block me-4">
                              <input
                                type="radio"
                                id={`language_${option?.value}`}
                                name="language"
                                className="form-check-input"
                                checked={formik.values.language === option?.value}
                                onChange={() => formik.setFieldValue('language', option?.value)}
                              />
                              <label className="form-check-label" htmlFor={`language_${option?.value}`}>
                                {option?.label}
                              </label>
                            </div>
                          ))}
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Form>
        </Container>
      </div>
    </>
  );
};

export default UpdateschoolFaq;
